import { PlusOutlined, PrinterOutlined } from "@ant-design/icons";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App, AutoComplete, Button, Card, Flex, Form, Input, Modal, Result, Skeleton, Space, Statistic, Table, Tag, Typography } from "antd";
import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { ApiError, api, unwrap, type Schemas } from "@/api/client";
import { useApplyFieldErrors, useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { ScanInput } from "@/components/ScanInput";
import { useTabTitle, usePoll } from "@/components/tabs/WorkTabs";
import { useI18n } from "@/i18n";
import { ExportButton } from "@/components/list/ExportButton";
import { DateRangeFilter, SearchFilter, StatusFilter, useSearchFilter, useStatusFilter } from "@/components/list/filters";
import { useDateRange, useListPage } from "@/components/list/useListPage";
import { NoWms, useWmsWarehouse } from "./common";
import { printPdf } from "./packLabel";

type Manifest = Schemas["Manifest"];
type ManifestParcel = NonNullable<Manifest["parcelList"]>[number];
const STATUSES = ["open", "done", "cancelled"] as const;
const STATUS_CHOICES = [...STATUSES, "all"] as const;

const statusColor: Record<string, string> = { open: "processing", done: "success", cancelled: "default" };

export function ManifestStatusTag({ status }: { status: Manifest["status"] }) {
  const { t } = useI18n();
  return <Tag color={statusColor[status]}>{t(`wms.mf.status.${status}`)}</Tag>;
}

/** Gudang > Serah terima: courier handovers per carrier (Plan §6.9). */
export function Manifests() {
  const { t, formatDateTime } = useI18n();
  const { me } = useAuth();
  const navigate = useNavigate();
  const { current, loading } = useWmsWarehouse();
  const status = useStatusFilter(STATUS_CHOICES, "open");
  const search = useSearchFilter();
  const range = useDateRange();
  const page = useListPage();
  const [creating, setCreating] = useState<string | null>(null);
  const tz = me?.workspace?.timezone;
  const filters = { warehouseId: current?.id ?? "", ...(status.value !== "all" ? { status: status.value } : {}), ...search.query, ...range.query };
  const list = useQuery({
    queryKey: ["manifests", filters, page.query],
    queryFn: () => unwrap(api.GET("/v1/manifests", { params: { query: { ...filters, ...page.query } } })),
    enabled: !!current,
    refetchInterval: usePoll(15_000),
    placeholderData: keepPreviousData,
  });
  const waiting = list.data?.waiting ?? [];

  if (!loading && !current) return <NoWms />;
  return (
    <>
      <Flex justify="space-between" align="center" wrap gap={12} style={{ marginBottom: 16 }}>
        <Typography.Title level={2} style={{ margin: 0, fontSize: 28 }}>
          {t("wms.mf.title")}
        </Typography.Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreating("")}>
          {t("wms.mf.add")}
        </Button>
      </Flex>
      <Flex gap={12} wrap style={{ marginBottom: 16 }}>
        <SearchFilter filter={search} placeholder={t("wms.mf.search")} />
        <StatusFilter
          filter={status}
          options={[...STATUSES.map((v) => ({ value: v, label: t(`wms.mf.status.${v}`) })), { value: "all" as const, label: t("list.allStatuses") }]}
        />
        <DateRangeFilter range={range} />
        <div style={{ flex: 1 }} />
        <ExportButton
          path="/v1/manifests/export"
          query={filters}
          filename={t("wms.mf.title")}
          columns={[
            { key: "number", label: t("wms.in.number") },
            { key: "carrier", label: t("orders.carrier") },
            { key: "status", label: t("common.status"), values: Object.fromEntries(STATUSES.map((v) => [v, t(`wms.mf.status.${v}`)])) },
            { key: "parcels", label: t("wms.mf.parcels"), kind: "number" },
            { key: "courierName", label: t("wms.mf.courier") },
            { key: "note", label: t("inventory.note") },
            { key: "createdBy", label: t("wms.count.createdBy") },
            { key: "createdAt", label: t("wms.count.createdAt"), kind: "datetime" },
            { key: "handedBy", label: t("wms.mf.handedBy") },
            { key: "handedAt", label: t("wms.mf.handedAt"), kind: "datetime" },
          ]}
        />
      </Flex>
      {waiting.length > 0 && (
        <Card size="small" title={t("wms.mf.waiting")} style={{ marginBottom: 16 }}>
          <Flex gap={16} wrap>
            {waiting.map((w) => (
              <Card key={w.carrier} size="small" style={{ minWidth: 200 }}>
                <Statistic title={w.carrier || t("wms.mf.noCarrier")} value={w.parcels} suffix={t("wms.mf.parcelsUnit")} />
                <Button size="small" style={{ marginTop: 8 }} onClick={() => setCreating(w.carrier)}>
                  {t("wms.mf.start")}
                </Button>
              </Card>
            ))}
          </Flex>
        </Card>
      )}
      <Table<Manifest>
        rowKey="id"
        loading={list.isFetching && !list.data}
        dataSource={list.data?.items ?? []}
        pagination={page.pagination(list.data)}
        locale={{ emptyText: t("wms.mf.empty") }}
        onRow={(m) => ({ onClick: () => navigate(`/app/warehouse/outbound/${m.id}`), style: { cursor: "pointer" } })}
        columns={[
          { title: t("wms.in.number"), dataIndex: "number", width: 150, render: (v: string) => <Typography.Text strong className="z-num">{v}</Typography.Text> },
          { title: t("orders.carrier"), dataIndex: "carrier" },
          { title: t("common.status"), dataIndex: "status", width: 130, render: (s: Manifest["status"]) => <ManifestStatusTag status={s} /> },
          { title: t("wms.mf.parcels"), dataIndex: "parcels", width: 100, align: "right" },
          { title: t("wms.mf.courier"), dataIndex: "courierName", width: 160, render: (v: string) => v || "—" },
          { title: t("wms.count.createdAt"), dataIndex: "createdAt", width: 170, render: (v: string) => formatDateTime(v, tz) },
          { title: t("wms.mf.handedAt"), dataIndex: "handedAt", width: 170, render: (v: string | null) => (v ? formatDateTime(v, tz) : "—") },
        ]}
      />
      {current && (
        <CreateManifest
          carrier={creating}
          carriers={waiting.map((w) => w.carrier).filter(Boolean)}
          warehouseId={current.id}
          onClose={() => setCreating(null)}
        />
      )}
    </>
  );
}

function CreateManifest({ carrier, carriers, warehouseId, onClose }: { carrier: string | null; carriers: string[]; warehouseId: string; onClose: () => void }) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const applyFieldErrors = useApplyFieldErrors();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [form] = Form.useForm<{ carrier: string; note?: string }>();
  const save = useMutation({
    mutationFn: (v: { carrier: string; note?: string }) =>
      unwrap(api.POST("/v1/manifests", { body: { warehouseId, carrier: v.carrier.trim(), note: v.note?.trim() ?? "" } })),
    onSuccess: (m) => {
      void queryClient.invalidateQueries({ queryKey: ["manifests"] });
      onClose();
      navigate(`/app/warehouse/outbound/${m.id}`);
    },
    onError: (err) => {
      if (!applyFieldErrors(form, err)) void message.error(errorText(err));
    },
  });
  return (
    <Modal
      open={carrier !== null}
      title={t("wms.mf.add")}
      okText={t("wms.mf.start")}
      cancelText={t("common.cancel")}
      confirmLoading={save.isPending}
      onOk={() => form.submit()}
      onCancel={onClose}
      destroyOnHidden
    >
      <Form form={form} layout="vertical" requiredMark={false} initialValues={{ carrier: carrier || undefined }} onFinish={(v) => save.mutate(v)}>
        <Form.Item name="carrier" label={t("orders.carrier")} extra={t("wms.mf.carrierHelp")} rules={[{ required: true, message: t("field.required") }]}>
          <AutoComplete options={carriers.map((c) => ({ value: c }))} placeholder={t("orders.carrier")} maxLength={100} />
        </Form.Item>
        <Form.Item name="note" label={t("wms.in.note")}>
          <Input.TextArea rows={2} maxLength={300} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

/** One handover: scan parcels onto it, then confirm with the courier's name. */
export function ManifestDetail() {
  const { manifestID = "" } = useParams();
  const { t, formatDateTime, formatNumber } = useI18n();
  const { me } = useAuth();
  const { message, modal } = App.useApp();
  const errorText = useErrorText();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const tz = me?.workspace?.timezone;
  const key = ["manifests", "detail", manifestID];
  const mf = useQuery({
    queryKey: key,
    queryFn: () => unwrap(api.GET("/v1/manifests/{manifestID}", { params: { path: { manifestID } } })),
    retry: (n, err) => !(err instanceof ApiError && err.status === 404) && n < 2,
  });
  useTabTitle(mf.data?.number);
  const done = (res: Manifest) => {
    queryClient.setQueryData(key, res);
    void queryClient.invalidateQueries({ queryKey: ["manifests"] });
  };
  const scan = useMutation({
    mutationFn: (code: string) => unwrap(api.POST("/v1/manifests/{manifestID}/scan", { params: { path: { manifestID } }, body: { code } })),
    onSuccess: (res, code) => {
      done(res);
      void message.success(t("wms.mf.scanned", { code }));
    },
    onError: (err) => void message.error(errorText(err)),
  });
  const remove = useMutation({
    mutationFn: (parcelID: string) => unwrap(api.DELETE("/v1/manifests/{manifestID}/parcels/{parcelID}", { params: { path: { manifestID, parcelID } } })),
    onSuccess: done,
    onError: (err) => void message.error(errorText(err)),
  });
  const cancel = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/manifests/{manifestID}/cancel", { params: { path: { manifestID } } })),
    onSuccess: done,
    onError: (err) => void message.error(errorText(err)),
  });

  const [printing, setPrinting] = useState(false);
  const printManifest = async () => {
    setPrinting(true);
    try {
      printPdf(await unwrap(api.GET("/v1/manifests/{manifestID}/pdf", { params: { path: { manifestID } }, parseAs: "blob" })));
    } catch (err) {
      void message.error(errorText(err));
    } finally {
      setPrinting(false);
    }
  };

  if (mf.isError) {
    return mf.error instanceof ApiError && mf.error.status === 404 ? <Result status="404" title={t("page.notFound.title")} /> : <Result status="error" title={errorText(mf.error)} />;
  }
  const m = mf.data;
  if (!m) return <Skeleton active />;
  const open = m.status === "open";
  return (
    <>
      <Flex justify="space-between" align="center" wrap gap={12} style={{ marginBottom: 16 }}>
        <Space align="center" size={12} wrap>
          <Typography.Title level={2} className="z-num" style={{ margin: 0, fontSize: 28 }}>
            {m.number}
          </Typography.Title>
          <ManifestStatusTag status={m.status} />
          <Typography.Text type="secondary">
            {m.carrier} · {m.createdBy} · {formatDateTime(m.createdAt, tz)}
          </Typography.Text>
        </Space>
        <Space wrap>
          {open && (
            <Button
              danger
              loading={cancel.isPending}
              onClick={() =>
                modal.confirm({
                  title: t("wms.mf.cancel"),
                  content: t("wms.mf.cancelConfirm", { number: m.number }),
                  okButtonProps: { danger: true },
                  okText: t("wms.mf.cancel"),
                  cancelText: t("common.close"),
                  onOk: () => cancel.mutateAsync(),
                })
              }
            >
              {t("wms.mf.cancel")}
            </Button>
          )}
          {open && (
            <Button type="primary" disabled={m.parcels === 0} onClick={() => setConfirming(true)}>
              {t("wms.mf.confirm", { n: m.parcels })}
            </Button>
          )}
          <Button icon={<PrinterOutlined />} loading={printing} onClick={() => void printManifest()}>
            {t("wms.mf.print")}
          </Button>
        </Space>
      </Flex>
      {m.status === "done" && (
        <Typography.Paragraph>
          {t("wms.mf.handedSummary", { courier: m.courierName, by: m.handedBy, at: m.handedAt ? formatDateTime(m.handedAt, tz) : "" })}
        </Typography.Paragraph>
      )}
      {open && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Space orientation="vertical" size={8} style={{ width: "100%" }}>
            <Typography.Text type="secondary">{t("wms.mf.scanHelp", { carrier: m.carrier })}</Typography.Text>
            <ScanInput onScan={(code) => scan.mutateAsync(code)} disabled={scan.isPending} placeholder={t("wms.mf.scanPlaceholder")} />
          </Space>
        </Card>
      )}
      <Card size="small" title={t("wms.mf.parcelsCount", { n: m.parcels })}>
        <Table<ManifestParcel>
          size="small"
          rowKey="id"
          dataSource={m.parcelList ?? []}
          pagination={false}
          columns={[
            { title: "#", width: 50, render: (_, __, i) => i + 1 },
            { title: t("wms.mf.tracking"), dataIndex: "trackingNo", render: (v: string) => <span className="z-num">{v || "—"}</span> },
            {
              title: t("orders.number"),
              dataIndex: "externalId",
              render: (v: string, p) => (
                <Space orientation="vertical" size={0}>
                  <span className="z-num">{v}</span>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {t(`platform.${p.platform}`)}
                  </Typography.Text>
                </Space>
              ),
            },
            { title: t("wms.pack.box"), dataIndex: "packagingCode", width: 110, render: (v: string) => <span className="z-num">{v || "—"}</span> },
            { title: t("wms.mf.weight"), dataIndex: "weightG", width: 100, align: "right", render: (v: number) => `${formatNumber(v)} g` },
            ...(open
              ? [
                  {
                    key: "remove",
                    width: 90,
                    render: (_: unknown, p: ManifestParcel) => (
                      <Button size="small" type="link" danger loading={remove.isPending && remove.variables === p.id} onClick={() => remove.mutate(p.id)}>
                        {t("wms.mf.remove")}
                      </Button>
                    ),
                  },
                ]
              : []),
          ]}
        />
      </Card>
      <ConfirmHandover manifest={confirming ? m : null} onClose={() => setConfirming(false)} onDone={done} />
    </>
  );
}

function ConfirmHandover({ manifest, onClose, onDone }: { manifest: Manifest | null; onClose: () => void; onDone: (m: Manifest) => void }) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const applyFieldErrors = useApplyFieldErrors();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<{ courierName: string }>();
  const save = useMutation({
    mutationFn: (v: { courierName: string }) =>
      unwrap(api.POST("/v1/manifests/{manifestID}/confirm", { params: { path: { manifestID: manifest!.id } }, body: { courierName: v.courierName.trim() } })),
    onSuccess: (res) => {
      onDone(res);
      for (const k of ["orders", "waves", "packing", "inventory", "locations"]) void queryClient.invalidateQueries({ queryKey: [k] });
      void message.success(t("wms.mf.handed", { n: res.parcels }));
      onClose();
    },
    onError: (err) => {
      if (!applyFieldErrors(form, err)) void message.error(errorText(err));
    },
  });
  return (
    <Modal
      open={manifest !== null}
      title={manifest ? t("wms.mf.confirmTitle", { number: manifest.number }) : ""}
      okText={t("wms.mf.confirmOk")}
      cancelText={t("common.cancel")}
      confirmLoading={save.isPending}
      onOk={() => form.submit()}
      onCancel={onClose}
      destroyOnHidden
    >
      <Typography.Paragraph type="secondary">{t("wms.mf.confirmBody", { n: manifest?.parcels ?? 0, carrier: manifest?.carrier ?? "" })}</Typography.Paragraph>
      <Form form={form} layout="vertical" requiredMark={false} onFinish={(v) => save.mutate(v)}>
        <Form.Item name="courierName" label={t("wms.mf.courier")} rules={[{ required: true, min: 2, message: t("field.required") }]}>
          <Input maxLength={100} autoFocus />
        </Form.Item>
      </Form>
    </Modal>
  );
}
